import { MessageRequest } from '@/types/chat/chat.types';

import { TargetStatusEnum } from '../c2/c2.types';
import { Coordinates, Coordinates3D, Mark } from '../commons/commons.types';
import { Procedure, ProcedureStateEnum, ProcedureStepTypeEnum } from '../config/procedure.types';
import { ClassificationEnum } from '../sensor/classification.types';
import { ReliabilityEnum } from '../sensor/identification.types';
import { AutomatonSegmentTypeEnum, EffectiveSensorStatusEnum, SensorTypeEnum, TrackLight } from '../sensor/sensors';
import { DroneClearanceEnum } from '../utm/drone.types';

export enum VerticalTrespassEnum {
  INSIDE_BOUNDARIES = 'INSIDE_BOUNDARIES',
  ABOVE_MAX_HEIGHT = 'ABOVE_MAX_HEIGHT',
  BELOW_MIN_HEIGHT = 'BELOW_MIN_HEIGHT',
}

export interface NotificationCriteria {
  site: string;
  platforms?: string[];
  page?: number;
  size?: number;
  unpaginated?: boolean;
  types?: NotificationTypeEnum[];
  notificationStatus?: NotificationStatusEnum;
  onlyEnded?: boolean;
  identifiers?: string[];
  displayIds?: string[];
  worstClassification?: ClassificationEnum[];
  from?: string;
  to?: string;
  minimumMaxHeight?: number;
  maximumMaxHeight?: number;
  reliability?: ReliabilityEnum;
  duration?: number;
  trackSensorUniqueKeys?: string[];
  zoneIds?: number[];
  worstSensorStatus?: EffectiveSensorStatusEnum[];
}

export enum NotificationFamilyEnum {
  LAD = 'LAD',
  PERIMETER = 'PERIMETER',
  TECHNICAL = 'TECHNICAL',
}

export enum NotificationFilterEnum {
  LAD = 'LAD',
  PERIMETER = 'PERIMETER',
  LAD_TECHNICAL = 'LAD_TECHNICAL',
  PERIMETER_TECHNICAL = 'PERIMETER_TECHNICAL',
}

export enum NotificationTypeEnum {
  DRONE = 'DRONE',
  SENSOR = 'SENSOR',
  SEGMENT = 'SEGMENT',
}

export enum NotificationStatusEnum {
  PENDING = 'PENDING',
  TERMINATED = 'TERMINATED',
}

export enum DroneAlertLevelEnum {
  DRONE_CRITICAL = 'DRONE_CRITICAL',
  DRONE_WARNING = 'DRONE_WARNING',
  DRONE_INFO = 'DRONE_INFO',
}

export enum SensorAlertLevelEnum {
  SENSOR_DISCONNECTED = 'SENSOR_DISCONNECTED',
  SENSOR_FAILURE = 'SENSOR_FAILURE',
  SENSOR_NON_DETECTION = 'SENSOR_NON_DETECTION',
  SENSOR_WARNING_NON_DETECTION = 'SENSOR_WARNING_NON_DETECTION',
}

export enum SegmentAlertLevelEnum {
  PERIMETER_EFFRACTION = 'PERIMETER_EFFRACTION',
}

export type AlertLevelEnum = DroneAlertLevelEnum | SensorAlertLevelEnum | SegmentAlertLevelEnum;

export const criticalAlertLevel: AlertLevelEnum[] = [
  DroneAlertLevelEnum.DRONE_CRITICAL,
  SegmentAlertLevelEnum.PERIMETER_EFFRACTION,
] as const;

export enum AlertTypeEnum {
  SENSOR_FAILURE = 'SENSOR_FAILURE',
  SENSOR_NON_DETECTION = 'SENSOR_NON_DETECTION',
  PERIMETER_EFFRACTION = 'PERIMETER_EFFRACTION',
  SPACE_VIOLATION = 'SPACE_VIOLATION',
  MANUAL = 'MANUAL',
  COLLISION = 'COLLISION',
}

export enum AlertContextEnum {
  OUTSIDE_FLIGHT_PLAN = 'OUTSIDE_FLIGHT_PLAN',
  FLIGHT_PLAN_VIOLATION = 'FLIGHT_PLAN_VIOLATION',
  NO_FLY_ZONE = 'NO_FLY_ZONE',
  NO_FLY_ZONE_3D = 'NO_FLY_ZONE_3D',
  COLLISION = 'COLLISION',
  TACTICAL_CLEARANCE = 'TACTICAL_CLEARANCE',
  SENSOR_FAILURE = 'SENSOR_FAILURE',
  SENSOR_WARNING_NON_DETECTION = 'SENSOR_WARNING_NON_DETECTION',
  SENSOR_NON_DETECTION = 'SENSOR_NON_DETECTION',
  PERIMETER_EFFRACTION = 'PERIMETER_EFFRACTION',
}

export enum ElevationReliabilityEnum {
  INCONSISTENT_WITH_DTM = 'INCONSISTENT_WITH_DTM',
  COMPUTED_WITH_DTM = 'COMPUTED_WITH_DTM',
  TRANSMITTED = 'TRANSMITTED',
  UNKNOWN = 'UNKNOWN',
}

type BaseNotification = {
  id: string;
  site: string;
  identifier: string;
  position: Coordinates;
  startTime: string;
  endTime: string | null;
  lastProcedureFirstAlertStartTime: string | null;
  lastProcedureClosingTime: string | null;
  alertCount: number;
  closable: boolean;
  status: NotificationStatusEnum;
  marks: Mark[];
  nbSteps: number;
  currentStepRank: number | null;
  currentStepType: ProcedureStepTypeEnum | null;
  procedureState: ProcedureStateEnum | null;
  muted: boolean;
  organization: string;
};

export type Elevation = {
  transmitted: number | null;
  computed: number | null;
  reliability: ElevationReliabilityEnum;
};

export type DroneNotificationLight = {
  type: NotificationTypeEnum.DRONE;
  worstAlertLevel: DroneAlertLevelEnum | null;
  currentWorstAlertLevel: DroneAlertLevelEnum | null;
  lastAlertLevel: DroneAlertLevelEnum | null;
  currentHeight: number;
  maxTransmittedAgl: number | null;
  maxComputedAgl: number | null;
  maxTransmittedAsl: number | null;
  maxComputedAsl: number | null;
  lastAltitude: number | null;
  hasFlightPlan: boolean; // ongoing
  hasHadActiveFlightPlan: boolean;
  withRemoteControl: boolean;
  hasDetectedRemoteControl: boolean;
  trackIds: TrackLight[];
  classification: ClassificationEnum;
  targetStatus: TargetStatusEnum;
  reliability: ReliabilityEnum;
  clearance: DroneClearanceEnum | null;
  occurrenceCount: number;
  displayId: string | null;
  lastDetectionTime: string | null;
  startPosition: Coordinates;
} & BaseNotification;

export type DroneNotification = {
  lastAltitude: number | null;
  trajectory: Coordinates[] | Coordinates3D[];
  elevationAgl: Elevation | null;
  elevationAsl: Elevation | null;
} & DroneNotificationLight;

export type SensorNotification = {
  type: NotificationTypeEnum.SENSOR;
  worstAlertLevel: SensorAlertLevelEnum;
  currentWorstAlertLevel: SensorAlertLevelEnum;
  lastAlertLevel: SensorAlertLevelEnum;
  sensorName: string;
  sensorType: SensorTypeEnum;
  sensorStatus: EffectiveSensorStatusEnum;
} & BaseNotification;

export type SegmentNotification = {
  type: NotificationTypeEnum.SEGMENT;
  worstAlertLevel: SegmentAlertLevelEnum;
  currentWorstAlertLevel: SegmentAlertLevelEnum;
  lastAlertLevel: SegmentAlertLevelEnum;
  segmentName: string;
  cabinetName: string;
  segmentStatus: EffectiveSensorStatusEnum;
  segmentType: AutomatonSegmentTypeEnum;
} & BaseNotification;

export type NotificationType = DroneNotificationLight | SensorNotification | SegmentNotification;

export enum NotificationSortEnum {
  START_TIME = 'START_TIME',
  DANGER = 'DANGER',
}

type BaseAlert = {
  id: number;
  site: string;
  identifier: string;
  startTime: string;
  endTime: string | null;
  parentId: string;
  procedureId: string;
  marks: Mark[];
};

export type SpaceViolationAlert = {
  type: AlertTypeEnum.SPACE_VIOLATION;
  context:
    | AlertContextEnum.OUTSIDE_FLIGHT_PLAN
    | AlertContextEnum.FLIGHT_PLAN_VIOLATION
    | AlertContextEnum.NO_FLY_ZONE
    | AlertContextEnum.NO_FLY_ZONE_3D;
  level: DroneAlertLevelEnum;
  zoneId: number;
  zoneName: string;
  heightMin: number | null;
  heightMax: number | null;
  verticalTrespass: VerticalTrespassEnum;
} & BaseAlert;

export type SensorFailureAlert = {
  type: AlertTypeEnum.SENSOR_FAILURE;
  context: AlertContextEnum.SENSOR_FAILURE;
  level: SensorAlertLevelEnum;
} & BaseAlert;

export type SensorNonDetectionAlert = {
  type: AlertTypeEnum.SENSOR_NON_DETECTION;
  context: AlertContextEnum.SENSOR_NON_DETECTION | AlertContextEnum.SENSOR_WARNING_NON_DETECTION;
  level: SensorAlertLevelEnum;
} & BaseAlert;

export type PerimeterEffractionAlert = {
  type: AlertTypeEnum.PERIMETER_EFFRACTION;
  context: AlertContextEnum.PERIMETER_EFFRACTION;
  level: SegmentAlertLevelEnum.PERIMETER_EFFRACTION;
} & BaseAlert;

export type CollisionAlert = {
  type: AlertTypeEnum.COLLISION;
  context: AlertContextEnum.COLLISION;
  level: DroneAlertLevelEnum;
  targetId2: string;
  safetyDistance: number;
} & BaseAlert;

export type ManualAlert = {
  type: AlertTypeEnum.MANUAL;
  context: AlertContextEnum;
  level: DroneAlertLevelEnum;
} & BaseAlert;

export type AlertType =
  | SpaceViolationAlert
  | ManualAlert
  | SensorFailureAlert
  | SensorNonDetectionAlert
  | PerimeterEffractionAlert
  | CollisionAlert;

export type NotificationPlatformDetail = {
  alerts: AlertType[];
  messages: MessageRequest[];
};

export type NotificationDetails = {
  notification: DroneNotification | SensorNotification | SegmentNotification;
  currentProcedure: Procedure | null;
  doneProcedures: Procedure[];
  detailsByPlatform: Record<string, NotificationPlatformDetail>;
};

export type DroneTrajectories = {
  id: string;
  identifier: string;
  displayId: string | null;
  reliability: ReliabilityEnum;
  startTime: string;
  endTime: string | null;
  maxTransmittedAgl: number | null;
  maxComputedAgl: number | null;
  maxTransmittedAsl: number | null;
  maxComputedAsl: number | null;
  worstAlertLevel: DroneAlertLevelEnum | null;
  trackIds: TrackLight[];
  trajectory: Coordinates[] | Coordinates3D[];
};

type NotificationDetailParameters = {
  withArchivedAlerts: boolean;
  withDoneProcedures: boolean;
};

export type NotificationDetailRequest = NotificationDetailParameters & { id: string };
export type NotificationDetailsBatchRequest = { ids: string[] };

export type AlertLight = {
  id: number;
  startTime: string;
  endTime: string | null;
  level: AlertLevelEnum;
};

export type AlertCriteria = {
  site: string;
  identifier: string;
  from: string;
  to: string;
};
